/**
 * Typed fetch wrapper for the Hangar API (api/openapi.yaml). Sends `Authorization: Bearer <id_token>`, validates
 * every response with a zod schema (@hangar/shared or src/api/schemas), maps `application/problem+json` to ApiError
 * (keeping X-Request-Id for toasts), and signs the user out on 401.
 */
import type { ZodType } from 'zod';
import { config } from '../config';
import { currentIdToken, dropSession } from '../auth/userManager';
import { adoptDefaultOrg, forgetRefusedChoice, orgHeaders, previewTarget, selectedOrg } from './orgSelection';

export interface FieldError {
  pointer: string;
  message: string;
  code?: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
    readonly errors: FieldError[] = [],
    readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
  /** "Something went wrong · req_…" for toasts. */
  get toastTitle(): string {
    return this.requestId ? `Something went wrong · ${this.requestId}` : 'Something went wrong';
  }
}

export const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

function fieldOfPointer(pointer: string): string | undefined {
  const p = pointer.replace(/^#/, '');
  if (p.startsWith('/query/')) return undefined;
  return p.split('/').filter(Boolean)[0];
}

type FieldPut = (field: string | undefined, message: unknown) => void;

function rawPointerOf(item: Record<string, unknown>): unknown {
  return item.pointer ?? item.field ?? (Array.isArray(item.path) ? `/${item.path.join('/')}` : item.path);
}

function putFromList(put: FieldPut, list: unknown[]): void {
  for (const item of list) {
    if (!isRecord(item)) continue;
    const raw = rawPointerOf(item);
    if (typeof raw === 'string') put(fieldOfPointer(raw), item.message);
  }
}

function putFromMap(put: FieldPut, d: Record<string, unknown>): void {
  for (const [k, v] of Object.entries(d)) {
    if (typeof v === 'string') put(k, v);
    else if (Array.isArray(v) && typeof v[0] === 'string') put(k, v[0]);
  }
}

function putFromDetails(put: FieldPut, d: unknown): void {
  if (Array.isArray(d)) putFromList(put, d);
  else if (isRecord(d)) {
    if (Array.isArray(d.issues)) putFromList(put, d.issues);
    if (Array.isArray(d.errors)) putFromList(put, d.errors);
    putFromMap(put, d);
  }
}

/**
 * Per-field messages from a Problem: OpenAPI `errors[]` (JSON pointers), or `details` as a zod-style issue list,
 * a `{ field: message }` map, or `{ issues: [...] }`.
 */
export function fieldErrorsOf(err: unknown): Record<string, string> {
  if (!(err instanceof ApiError)) return {};
  const out: Record<string, string> = {};
  const put: FieldPut = (field, message) => {
    if (field && typeof message === 'string' && !out[field]) out[field] = message;
  };
  for (const e of err.errors) put(fieldOfPointer(e.pointer), e.message);
  putFromDetails(put, err.details);
  return out;
}

/** `context.missing[]`, `context.allowedTracks[]`… of a Problem (HGR-ENV-MISSING-REQUIRED lists the keys there). */
export function problemContext(err: unknown): Record<string, unknown> {
  return err instanceof ApiError && isRecord(err.details) ? err.details : {};
}

async function toApiError(res: Response): Promise<ApiError> {
  const headerId = res.headers.get('x-request-id') ?? undefined;
  let body: unknown = null;
  try {
    body = await res.json();
  } catch {
    body = null;
  }
  const b = isRecord(body) ? body : {};
  const code = typeof b.code === 'string' ? b.code : `HTTP-${res.status}`;
  const message = [b.detail, b.title, b.message].find((v): v is string => typeof v === 'string' && v.length > 0) ?? `Request failed (${res.status})`;
  const requestId = typeof b.requestId === 'string' ? b.requestId : headerId;
  const errors: FieldError[] = Array.isArray(b.errors)
    ? b.errors.filter((e): e is FieldError => isRecord(e) && typeof e.pointer === 'string' && typeof e.message === 'string')
    : [];
  return new ApiError(res.status, code, message, requestId, errors, b.details ?? b.context);
}

/** Query values; an array repeats the parameter (`status=running&status=queued`, OpenAPI `explode: true`). */
export type Query = Record<string, string | number | boolean | string[] | undefined>;

export interface RequestOptions<T> {
  schema: ZodType<T>;
  body?: unknown;
  query?: Query;
  idempotencyKey?: string;
  /** `If-Match` for PATCH/DELETE (the resource's `etag`). */
  ifMatch?: string;
  /** Request content type; PATCH endpoints take `application/merge-patch+json`. */
  contentType?: string;
  signal?: AbortSignal;
}

export function apiUrl(path: string, query?: Query): URL {
  const url = new URL(`/v1${path}`, `${config.apiUrl}/`);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined) continue;
      if (Array.isArray(v)) for (const item of v) url.searchParams.append(k, item);
      else url.searchParams.set(k, String(v));
    }
  }
  return url;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

async function requireToken(): Promise<string> {
  const token = await currentIdToken();
  if (!token) {
    void dropSession('expired');
    throw new ApiError(401, 'HGR-AUTH-REQUIRED', 'Not signed in');
  }
  return token;
}

function requestHeaders(method: Method, token: string, opts: RequestOptions<unknown>): Record<string, string> {
  const headers: Record<string, string> = { Authorization: `Bearer ${token}`, Accept: 'application/json, application/problem+json', ...orgHeaders() };
  if (opts.body !== undefined) headers['Content-Type'] = opts.contentType ?? (method === 'PATCH' ? 'application/merge-patch+json' : 'application/json');
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;
  if (opts.ifMatch) headers['If-Match'] = opts.ifMatch;
  return headers;
}

/** `fetch` that maps a network failure to HGR-NETWORK (an abort is rethrown untouched). */
async function fetchOrNetworkError(url: URL, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init);
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e;
    throw new ApiError(0, 'HGR-NETWORK', `Could not reach the API at ${config.apiUrl}`);
  }
}

/** Throws the ApiError of a non-2xx answer; a 401 also drops the session. */
async function throwIfNotOk(res: Response): Promise<void> {
  if (res.status === 401) {
    const err = await toApiError(res);
    void dropSession('unauthorized');
    throw err;
  }
  if (!res.ok) {
    const err = await toApiError(res);
    // A stale org or preview choice: forget it and start over from the overview in the default org.
    if (forgetRefusedChoice(err.status, err.message)) window.location.assign('/');
    throw err;
  }
}

async function parseBody<T>(res: Response, schema: ZodType<T>): Promise<T> {
  const requestId = res.headers.get('x-request-id') ?? undefined;
  const json: unknown = res.status === 204 ? null : await res.json();
  const parsed = schema.safeParse(json);
  if (!parsed.success) {
    throw new ApiError(res.status, 'HGR-CLIENT-SCHEMA', 'The API answered with an unexpected shape', requestId, [], parsed.error.issues);
  }
  return parsed.data;
}

/**
 * A user in several orgs needs `X-Hangar-Org` on every call but `GET /me`, which is how the console learns the org it
 * landed in. Before the first call of a page load without a remembered choice, ask `/me` once and adopt its org, so no
 * call is refused (400) while the console is still finding out. A failure is not cached: the next call asks again.
 */
let orgKnown: Promise<void> | undefined;
function ensureOrgKnown(token: string): Promise<void> {
  orgKnown ??= (async () => {
    const res = await fetchOrNetworkError(apiUrl('/me'), { headers: requestHeaders('GET', token, {} as RequestOptions<unknown>), cache: 'no-store' });
    const me: unknown = res.ok ? await res.json() : null;
    if (isRecord(me) && isRecord(me.org) && typeof me.org.id === 'string') adoptDefaultOrg({ org: { id: me.org.id }, orgs: Array.isArray(me.orgs) ? me.orgs : undefined, preview: me.preview === true });
  })().catch(() => { orgKnown = undefined; });
  return orgKnown;
}

/** Resolves once the org of the next call is known (callers that fetch by hand, such as the event stream). */
export async function orgReady(token: string): Promise<void> {
  if (!selectedOrg() && !previewTarget()) await ensureOrgKnown(token);
}

export async function api<T>(method: Method, path: string, opts: RequestOptions<T>): Promise<T> {
  const token = await requireToken();
  if (path !== '/me') await orgReady(token);
  const res = await fetchOrNetworkError(apiUrl(path, opts.query), {
    method,
    headers: requestHeaders(method, token, opts),
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    signal: opts.signal ?? null,
    cache: 'no-store',
  });
  await throwIfNotOk(res);
  return parseBody(res, opts.schema);
}

/** The API-relative path (`/…?query`) of a download link; anything but a `/v1/…` route is rejected. */
function apiPathOfLink(link: string): string {
  try {
    const url = new URL(link, `${config.apiUrl}/`);
    if (!url.pathname.startsWith('/v1/')) throw new Error('not an API route');
    return `${url.pathname.slice('/v1'.length)}${url.search}`;
  } catch {
    throw new ApiError(0, 'HGR-CLIENT-SCHEMA', 'The download link is not a Hangar API route');
  }
}

/**
 * The bytes behind an API download link (`PresignedUrl.requiresAuth`, e.g. an artifact on a local Hangar). The bearer
 * token travels in the `Authorization` header, never in the URL, and only to this console's API: the link's `/v1/…`
 * path is re-based on `VITE_API_URL`, whatever host the API wrote into it.
 */
export async function apiBlob(link: string, signal?: AbortSignal): Promise<Blob> {
  const path = apiPathOfLink(link);
  const token = await requireToken();
  const res = await fetchOrNetworkError(apiUrl(path), { headers: { Authorization: `Bearer ${token}`, Accept: '*/*', ...orgHeaders() }, signal: signal ?? null, cache: 'no-store' });
  await throwIfNotOk(res);
  return res.blob();
}
